import { useEffect } from "react";
import { MapContainer, TileLayer, Marker, Polygon, Popup, useMap } from "react-leaflet";
import L from "leaflet";

const STATUT_COLOR = {
  "sécurisé": "#10b981",
  "en litige": "#ef4444",
  "en cours": "#f59e0b",
};

function pinIcon(color) {
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="30" height="42" viewBox="0 0 30 42">
      <path d="M15 0C6.7 0 0 6.7 0 15c0 10.5 15 27 15 27s15-16.5 15-27C30 6.7 23.3 0 15 0z" fill="${color}" stroke="#ffffff" stroke-width="2"/>
      <circle cx="15" cy="15" r="5.5" fill="#ffffff"/>
    </svg>`;
  return L.divIcon({ html: svg, className: "parcel-pin", iconSize: [30, 42], iconAnchor: [15, 42], popupAnchor: [0, -38] });
}

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (points.length === 1) map.setView(points[0], 12);
    else if (points.length > 1) map.fitBounds(points, { padding: [50, 50], maxZoom: 13 });
  }, [points, map]);
  return null;
}

const popupContent = (p) => (
  <Popup>
    <strong>{p.reference}</strong><br />
    {p.proprietaire}<br />
    {p.localite}, {p.region}<br />
    {p.superficie} ha — {p.statut}
  </Popup>
);

export default function ParcelMap({ parcels }) {
  const hasPoly = (p) => Array.isArray(p.boundary) && p.boundary.length >= 3;
  const withPoly = parcels.filter(hasPoly);
  // Point markers only for parcels that have coords but NO polygon
  const pointOnly = parcels.filter(
    (p) => !hasPoly(p) && typeof p.latitude === "number" && typeof p.longitude === "number"
  );

  const boundsPoints = [
    ...withPoly.flatMap((p) => p.boundary),
    ...pointOnly.map((p) => [p.latitude, p.longitude]),
  ];
  const located = withPoly.length + pointOnly.length;
  const center = boundsPoints[0] || [7.54, -5.55];

  return (
    <div className="relative isolate rounded-xl overflow-hidden border border-border" data-testid="parcel-map">
      <MapContainer center={center} zoom={boundsPoints.length ? 8 : 6} style={{ height: 400, width: "100%" }} scrollWheelZoom={false}>
        <TileLayer attribution="&copy; OpenStreetMap" url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
        <FitBounds points={boundsPoints} />

        {withPoly.map((p) => {
          const color = STATUT_COLOR[p.statut] || "#6b7a5e";
          return (
            <Polygon key={p.id} positions={p.boundary}
              pathOptions={{ color, fillColor: color, fillOpacity: 0.28, weight: 2 }}>
              {popupContent(p)}
            </Polygon>
          );
        })}

        {pointOnly.map((p) => (
          <Marker key={p.id} position={[p.latitude, p.longitude]} icon={pinIcon(STATUT_COLOR[p.statut] || "#6b7a5e")}>
            {popupContent(p)}
          </Marker>
        ))}
      </MapContainer>

      <div className="absolute top-3 right-3 z-[500] bg-background/90 backdrop-blur border border-border rounded-lg px-3 py-2.5 text-xs pointer-events-none" data-testid="map-legend">
        <p className="font-medium mb-2 text-foreground">
          {located}/{parcels.length} parcelle{parcels.length > 1 ? "s" : ""} cartographiée{located > 1 ? "s" : ""}
        </p>
        <div className="space-y-1">
          {Object.entries(STATUT_COLOR).map(([label, color]) => (
            <div key={label} className="flex items-center gap-2 text-muted-foreground">
              <span className="w-2.5 h-2.5 rounded-full" style={{ background: color }} />
              <span className="capitalize">{label}</span>
            </div>
          ))}
        </div>
      </div>

      {located === 0 && (
        <div className="absolute inset-x-0 bottom-3 z-[500] flex justify-center pointer-events-none">
          <span className="bg-background/90 backdrop-blur border border-border rounded-full px-4 py-2 text-xs text-muted-foreground">
            Aucune parcelle cartographiée — tracez les limites ou ajoutez des coordonnées.
          </span>
        </div>
      )}
    </div>
  );
}
